// Console header (56 px): wordmark + product crumb + the current-site switcher
// on the left (the switcher is hidden below `sm` — phones get it in the nav
// drawer and the tab bar's "More" sheet), the global search (⌘K) centred, and product switcher / system status / notifications /
// tenant context / avatar menu on the right. The status dot reads the shared
// feed-health poll owned by AppShell (useFeedHealth) — no extra request.
// Every icon control is 36 px with a fine pointer and 44 px under a coarse one
// (`coarse:` — any viewport width, landscape phones included).

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, LayoutGrid, LogOut, Menu, Search, Settings as SettingsIcon, User } from "lucide-react";

import { PowerBESSLogo } from "@/components/PowerBESSLogo";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useWsClient } from "@/lib/ws/WsProvider";
import type { ConsoleApp } from "@/app/products";
import { Kbd, ProductIcon, StatusDot, Tooltip, type SemanticTone } from "@/app/console/ui";
import type { FeedHealth } from "@/app/console/useFeedHealth";
import { personaForRole } from "@/app/orgConstants";
import { SiteSwitcher } from "@/app/site/SiteSwitcher";

interface HeaderProps {
  app: ConsoleApp;
  feedHealth: FeedHealth;
  onOpenAppDrawer: () => void;
  onOpenSearch: () => void;
  onOpenMobileNav: () => void;
}

// One focus indicator only: the lime ring (the global :focus-visible outline is
// switched off here so a ring and an outline never stack).
const FOCUS = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60";
const ICON_BTN = `flex h-9 w-9 shrink-0 items-center justify-center rounded-control text-text-2 transition-colors duration-fast hover:bg-white/[0.06] hover:text-text-1 coarse:h-11 coarse:w-11 ${FOCUS}`;

export function Header({ app, feedHealth, onOpenAppDrawer, onOpenSearch, onOpenMobileNav }: HeaderProps) {
  const { user, roles } = useAuth();
  const isPlatformAdmin = roles.includes("platform_admin");

  return (
    <header className="relative z-30 flex h-header shrink-0 select-none items-center gap-1 border-b border-line bg-surface-1 px-2 sm:gap-3 sm:px-4" style={{ paddingTop: "var(--pb-safe-top)" }}>
      {/* Left zone: mobile nav toggle + wordmark + product crumb */}
      <div className="flex min-w-0 shrink-0 items-center gap-1 sm:shrink sm:gap-2">
        <button type="button" onClick={onOpenMobileNav} className={`${ICON_BTN} lg:hidden`} aria-label="Open navigation">
          <Menu className="h-4 w-4" />
        </button>

        <Link
          to="/overview"
          aria-label="PowerBESS — Overview"
          className={`flex h-9 shrink-0 items-center gap-2 rounded-control px-1 transition-colors duration-fast hover:bg-white/[0.04] coarse:h-11 coarse:min-w-11 ${FOCUS}`}
        >
          <PowerBESSLogo size={24} />
          <span className="hidden text-[15px] font-semibold tracking-tight text-text-1 sm:inline">
            Power<span className="text-brand">BESS</span>
          </span>
        </Link>
        <span className="flex min-w-0 items-center gap-2" aria-label={`Current product: ${app.name}`}>
          <span className="hidden text-text-3 sm:inline" aria-hidden="true">
            /
          </span>
          <ProductIcon icon={app.icon} accent={app.accent} size="xs" />
          <span className="hidden truncate text-sm font-medium text-text-1 sm:inline">{app.name}</span>
        </span>
        {/* Current site — product-neutral, truncates at 220 px. */}
        <SiteSwitcher className="ml-1 hidden sm:inline-flex" />
      </div>

      {/* Centre zone: global search. Below sm it collapses to an icon button. */}
      <div className="flex min-w-0 flex-1 justify-end sm:justify-center sm:px-4">
        <button
          type="button"
          onClick={onOpenSearch}
          className={`flex h-9 w-9 shrink-0 items-center justify-center gap-2 rounded-control border border-transparent bg-transparent text-left text-sm text-text-2 transition-colors duration-fast hover:bg-white/[0.06] hover:text-text-1 coarse:h-11 coarse:min-w-11 sm:w-full sm:max-w-xl sm:justify-start sm:border-line sm:bg-surface-0 sm:px-3 sm:text-text-3 sm:hover:border-line-strong sm:hover:bg-surface-0 sm:hover:text-text-2 ${FOCUS}`}
          aria-label="Open search (Command-K)"
        >
          <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="hidden flex-1 truncate sm:block">Search pages, assets, actions…</span>
          <Kbd className="hidden sm:inline-flex coarse:hidden">⌘K</Kbd>
        </button>
      </div>

      {/* Right zone */}
      <div className="flex shrink-0 items-center gap-0 sm:gap-1">
        <Tooltip content="Switch product · ⌘1–7">
          <button type="button" onClick={onOpenAppDrawer} className={`${ICON_BTN} hidden sm:flex`} aria-label="Open app launcher">
            <LayoutGrid className="h-4 w-4" />
          </button>
        </Tooltip>
        <SystemStatus health={feedHealth} />
        <NotificationsBell />
        {isPlatformAdmin && user && <TenantBadge tenantSlug={user.tenantSlug} />}
        <ProfileMenu />
      </div>
    </header>
  );
}

// ---------------------------------------------------------------------------
// System status dot — green all feeds live · amber n stale · red outage
// ---------------------------------------------------------------------------

function SystemStatus({ health }: { health: FeedHealth }) {
  const navigate = useNavigate();
  const tone: SemanticTone =
    health.status === "live" ? "ok" : health.status === "stale" ? "warn" : health.status === "outage" ? "danger" : "neutral";
  const n = health.unhealthy.length;
  const text =
    health.status === "live"
      ? `All ${health.feeds.length} feeds live`
      : health.status === "stale"
        ? `${n} feed${n === 1 ? "" : "s"} stale`
        : health.status === "outage"
          ? `${health.offline.length} feed${health.offline.length === 1 ? "" : "s"} offline${health.stale.length ? ` · ${health.stale.length} stale` : ""}`
          : health.error
            ? "Feed status unavailable"
            : "Checking feeds…";
  return (
    <Tooltip content={`${text} — open feed health`}>
      <button
        type="button"
        onClick={() => navigate("/data")}
        className={`hidden h-9 w-7 shrink-0 items-center justify-center rounded-control sm:flex transition-colors duration-fast hover:bg-white/[0.06] coarse:h-11 coarse:w-11 ${FOCUS}`}
        aria-label={`System status: ${text}`}
      >
        <StatusDot tone={tone} pulse={health.status === "outage"} />
      </button>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Tenant context badge (platform_admin only)
// ---------------------------------------------------------------------------

function TenantBadge({ tenantSlug }: { tenantSlug: string }) {
  return (
    <div
      className="mx-1 hidden h-7 items-center gap-1.5 rounded-md border border-line bg-surface-0 px-2 text-xs md:flex"
      title="Active tenant context"
    >
      <span className="text-text-3">Tenant</span>
      <span className="font-code text-[11px] font-medium text-text-2">{tenantSlug}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notifications bell
// ---------------------------------------------------------------------------

function NotificationsBell() {
  const { unreadAlerts, clearAlertBadge } = useWsClient();
  const navigate = useNavigate();

  return (
    <Tooltip content="Notifications — open the alert feed">
      <button
        type="button"
        className={`relative ${ICON_BTN}`}
        aria-label={`Notifications${unreadAlerts > 0 ? ` — ${unreadAlerts} unread` : ""}`}
        onClick={() => {
          clearAlertBadge();
          navigate("/asset/alerts");
        }}
      >
        <Bell className="h-4 w-4" />
        {unreadAlerts > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-danger px-1 font-mono text-[9px] font-semibold text-black coarse:right-1.5 coarse:top-1.5">
            {unreadAlerts > 99 ? "99+" : unreadAlerts}
          </span>
        )}
      </button>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Profile menu
// ---------------------------------------------------------------------------

function ProfileMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;

  const initials = user.email.slice(0, 2).toUpperCase();
  const accent = personaForRole(user.role).accent;

  async function onLogout() {
    setOpen(false);
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="relative sm:ml-1" ref={ref}>
      <button
        type="button"
        className={`group flex h-9 w-9 items-center justify-center rounded-control coarse:h-11 coarse:w-11 ${FOCUS}`}
        aria-label="Open profile menu"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((o) => !o)}
      >
        <span
          className="flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-semibold transition-shadow duration-fast group-hover:shadow-[0_0_0_2px_var(--pb-line-strong)]"
          style={{ backgroundColor: `${accent}22`, color: accent, boxShadow: `inset 0 0 0 1px ${accent}55` }}
          aria-hidden="true"
        >
          {initials}
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-10 z-menu min-w-[240px] rounded-card border border-line-strong bg-surface-3 py-1 shadow-[0_16px_48px_rgba(0,0,0,0.5)] coarse:top-12"
        >
          <div className="border-b border-line px-4 py-3">
            <div className="truncate text-sm font-medium text-text-1">{user.email}</div>
            <div className="mt-0.5 text-xs capitalize text-text-3">{user.role.replace(/_/g, " ")}</div>
            <div className="mt-0.5 text-xs text-text-3">
              Tenant <span className="font-code text-text-2">{user.tenantSlug}</span>
            </div>
          </div>
          <MenuButton icon={<User className="h-4 w-4" />} label="Account settings" onClick={() => { setOpen(false); navigate("/settings"); }} />
          <MenuButton icon={<SettingsIcon className="h-4 w-4" />} label="Organisation" onClick={() => { setOpen(false); navigate("/org"); }} />
          <MenuButton icon={<LogOut className="h-4 w-4" />} label="Sign out" danger onClick={onLogout} />
        </div>
      )}
    </div>
  );
}

function MenuButton({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      className={[
        "flex h-9 w-full items-center gap-3 px-4 text-sm text-text-2 transition-colors duration-fast hover:bg-white/[0.06] coarse:h-11",
        danger ? "hover:text-danger" : "hover:text-text-1",
      ].join(" ")}
      onClick={onClick}
    >
      {icon}
      {label}
    </button>
  );
}
